import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import {
  DangerousOutlined,
  FilterAltOutlined,
  HelpOutline,
  PaidOutlined
} from '@mui/icons-material';
import {
  LinearProgress,
  Stack,
  Typography,
  Skeleton,
  Grid,
  Badge
} from '@mui/material';
import {
  AxisConfig,
  BarChart,
  ChartsYAxisProps,
  PieChart
} from '@mui/x-charts';

import Page from '@/components/Page';
import Button from '@/components/button';
import DashboardCard from '@/components/dashboardCard';
import Header from '@/components/header';
import TreeIcon from '@/components/icons/TreeIcon';
import DotIcon from '@/components/icons/compliance/DotIcon';
import { dashboardSteps } from '@/constants/driverSteps';
import { useFetchWorkspaceStatistics } from '@/hooks/queries/workspaces';
import { useUrlParams } from '@/hooks/urlParams';
import useDriver from '@/hooks/useDriver';
import { Evaluation } from '@/models/evaluations';
import getRoute from '@/utils/getRoute';

import ChartCard from '../evaluations/statistics/_partials/ChartCard';
import ExpandableLegend from '../evaluations/statistics/_partials/ExpandableLegend';
import PieCenterLabel from '../evaluations/statistics/_partials/PieCenterLabel';
import FilterDrawer from './_partials/FilterDrawer';

// Helper function to safely get values or return a default
const safeValue = (value: number | undefined, fallback = 0) =>
  value ?? fallback;

// Memoize these values to avoid recreating them on every render
const useChartData = (
  workspaceStatistics?: Evaluation.Statistics,
  keywordSlice?: number
) =>
  useMemo(() => {
    const { youtube, instagram, tiktok } = workspaceStatistics?.platform || {};
    const types = [
      {
        label: 'Videos',
        value: safeValue(youtube?.video),
        color: '#E74A2E',
        key: 'Youtube'
      },
      {
        label: 'Shorts',
        value: safeValue(youtube?.short),
        color: '#921B1C',
        key: 'Youtube'
      },
      {
        label: 'Stories',
        value: safeValue(instagram?.story),
        color: '#B26600',
        key: 'Instagram'
      },
      {
        label: 'Reels',
        value: safeValue(instagram?.reel),
        color: '#AE8F67',
        key: 'Instagram'
      },
      {
        label: 'Posts',
        value: safeValue(instagram?.post),
        color: '#FF9200',
        key: 'Instagram'
      },
      {
        label: 'Post',
        value: safeValue(tiktok?.post),
        color: '#4A9FE8',
        key: 'Tik Tok'
      },
      {
        label: 'Story',
        value: safeValue(tiktok?.story),
        color: '#6254C7',
        key: 'Tik Tok'
      }
    ];

    const platforms = [
      {
        label: 'Youtube',
        value: safeValue(youtube?.total),
        color: '#EA5455'
      },
      {
        label: 'Instagram',
        value: safeValue(instagram?.total),
        color: '#FFB44F'
      },
      {
        label: 'Tik Tok',
        value: safeValue(tiktok?.total),
        color: '#326CCB'
      }
    ];

    const reviewedPosts = [
      {
        label: 'Rated',
        value: safeValue(workspaceStatistics?.rated),
        color: '#000000'
      },
      {
        label: 'In evaluations',
        value: safeValue(workspaceStatistics?.total),
        color: '#5C5C5C'
      },
      {
        label: 'Total',
        value: safeValue(workspaceStatistics?.totalPosts),
        color: '#8c8989'
      }
    ];

    const keywordsLength = Object.keys(
      workspaceStatistics?.keywords || {}
    ).length;

    const allKeywords = Object.keys(workspaceStatistics?.keywords || {})
      .map((keyword) => ({
        value: safeValue(workspaceStatistics?.keywords[keyword]),
        keyword
      }))
      .sort((a, b) => b.value - a.value);

    const keywordsToShow = allKeywords.slice(0, keywordSlice);

    const disclosuredPercentage =
      parseFloat(
        (
          (safeValue(workspaceStatistics?.disclosured) * 100) /
          safeValue(workspaceStatistics?.total, 1)
        ).toFixed(2)
      ) || 0;

    return {
      platforms,
      types,
      reviewedPosts,
      allKeywords,
      keywordsToShow,
      keywordsLength,
      disclosuredPercentage
    };
  }, [workspaceStatistics, keywordSlice]);

// Create reusable function for generating dashboard items
const useDashboardCardItems = (workspaceStatistics?: Evaluation.Statistics) =>
  useMemo(() => {
    const { adType, rated } = workspaceStatistics || {};
    const generatePercentage = (value: number | undefined) =>
      value
        ? parseFloat(
            ((safeValue(value) * 100) / safeValue(rated, 1)).toFixed(1)
          )
        : 0;

    return [
      {
        icon: (
          <DangerousOutlined sx={{ color: '#fff', width: 36, height: 36 }} />
        ),
        title: 'Not Relevant',
        value: safeValue(adType?.notRelevant),
        description: 'rated posts are Not Relevant',
        descriptionPercentage: generatePercentage(adType?.notRelevant)
      },
      {
        icon: <TreeIcon sx={{ color: '#fff', width: 36, height: 36 }} />,
        title: 'Organic Posts',
        value: safeValue(adType?.organic),
        description: 'rated posts are Organic',
        descriptionPercentage: generatePercentage(adType?.organic)
      },
      {
        icon: <HelpOutline sx={{ color: '#fff', width: 36, height: 36 }} />,
        title: 'Questionable Posts',
        value:
          safeValue(adType?.questionable?.compliant) +
          safeValue(adType?.questionable?.questionable) +
          safeValue(adType?.questionable?.violation),
        description: 'rated posts are Questionable',
        descriptionPercentage: generatePercentage(
          safeValue(adType?.questionable?.compliant) +
            safeValue(adType?.questionable?.questionable) +
            safeValue(adType?.questionable?.violation)
        ),
        items: [
          {
            icon: <DotIcon fontSize="inherit" color="error" />,
            text: 'Violation',
            value: adType?.questionable?.violation
          },
          {
            icon: <DotIcon fontSize="inherit" color="warning" />,
            text: 'Questionable',
            value: adType?.questionable?.questionable
          },
          {
            icon: <DotIcon fontSize="inherit" color="success" />,
            text: 'Compliant',
            value: adType?.questionable?.compliant
          }
        ]
      },
      {
        icon: <PaidOutlined sx={{ color: '#fff', width: 36, height: 36 }} />,
        title: 'Advertisement Posts',
        value:
          safeValue(adType?.advertisement?.compliant) +
          safeValue(adType?.advertisement?.questionable) +
          safeValue(adType?.advertisement?.violation),
        description: 'rated posts are Advertisement',
        descriptionPercentage: generatePercentage(
          safeValue(adType?.advertisement?.compliant) +
            safeValue(adType?.advertisement?.questionable) +
            safeValue(adType?.advertisement?.violation)
        ),
        items: [
          {
            icon: <DotIcon fontSize="inherit" color="error" />,
            text: 'Violation',
            value: adType?.advertisement?.violation
          },
          {
            icon: <DotIcon fontSize="inherit" color="warning" />,
            text: 'Questionable',
            value: adType?.advertisement?.questionable
          },
          {
            icon: <DotIcon fontSize="inherit" color="success" />,
            text: 'Compliant',
            value: adType?.advertisement?.compliant
          }
        ]
      }
    ];
  }, [workspaceStatistics]);

const defaultFilterParams = {
  adType: undefined,
  compliance: undefined,
  platform: undefined,
  createTime: undefined, // Start and end date in 'YYYY-MM-DD,YYYY-MM-DD' format
  keywords: undefined,
  evaluationIds: undefined,
  labels: undefined,
  creatorIds: undefined,
  creatorGroupIds: undefined
};

const WorkspaceDashboard = () => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [keywordSlice, setKeywordSlice] = useState<number | undefined>(7);
  const { search } = useLocation();

  const urlParams = useUrlParams(
    getRoute.dashboard.WORKSPACE(),
    defaultFilterParams
  );

  const { data: workspaceStatistics, isLoading: isStatisticsLoading } =
    useFetchWorkspaceStatistics({
      params: { ...urlParams.params }
    });

  const {
    platforms,
    types,
    reviewedPosts,
    allKeywords,
    keywordsToShow,
    keywordsLength,
    disclosuredPercentage
  } = useChartData(workspaceStatistics, keywordSlice);
  const dashboardCardItems = useDashboardCardItems(workspaceStatistics);

  const keywordsHeight = useMemo(
    () => Math.max((keywordSlice || keywordsToShow.length) * 40, 450),
    [keywordsToShow, keywordSlice]
  );

  // Calculate dynamic left margin based on longest keyword
  const keywordsMarginLeft = useMemo(() => {
    if (!keywordsToShow.length) return 100; // default margin

    // Find the longest keyword length
    const longestKeywordLength = Math.max(
      ...keywordsToShow.map((item) => item.keyword.length)
    );

    // Adjust the margin based on the length of the longest keyword
    const baseMargin = 100; // minimum margin
    const marginIncrementPerCharacter = 5; // adjust as needed

    return baseMargin + longestKeywordLength * marginIncrementPerCharacter;
  }, [keywordsToShow]);

  useDriver({
    isLoading: isStatisticsLoading,
    steps: dashboardSteps
  });

  const MIN_SLICE_RATIO = 0.02;

  const largestValue = Math.max(...reviewedPosts.map(({ value }) => value));
  const minSliceValue = largestValue * MIN_SLICE_RATIO;

  const reviewedSlices = reviewedPosts.map((post) => ({
    ...post,
    value: Math.max(post.value, minSliceValue)
  }));

  return (
    <Page
      title="Dashboard"
      header={
        <Header
          title="Dashboard"
          actions={
            <Badge
              color="secondary"
              badgeContent={search !== '' ? '' : undefined}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
            >
              <Button
                id="dashboard-bttn-filter"
                loading={isStatisticsLoading}
                onClick={() => setDrawerOpen(true)}
                text="filter"
                startIcon={<FilterAltOutlined />}
              />
            </Badge>
          }
        />
      }
    >
      <Stack gap={3}>
        {isStatisticsLoading ? (
          <>
            <Stack
              direction="row"
              flexWrap="wrap"
              mt={1.5}
              gap={3}
              justifyContent="space-between"
            >
              {Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton
                  key={idx}
                  sx={{
                    flexGrow: 1,
                    flexBasis: 0,
                    minWidth: '314px'
                  }}
                  variant="rectangular"
                  height={150}
                />
              ))}
            </Stack>
            <Stack direction="row" gap={3}>
              <Skeleton variant="rectangular" height={600} width="100%" />
              <Skeleton variant="rectangular" height={600} width="100%" />
            </Stack>
            <Skeleton variant="rectangular" height={580} width="100%" />
          </>
        ) : (
          <>
            {/* Dashboard Cards */}
            <Stack
              direction="row"
              flexWrap="wrap"
              mt={1.5}
              gap={3}
              justifyContent="space-between"
              id="dashboard-cards"
            >
              {dashboardCardItems.map((item, index) => (
                <DashboardCard key={index} {...item} />
              ))}
            </Stack>

            {/* Charts Row */}
            <Grid container item xs={12} spacing={3}>
              {/* Posts per Platform */}
              <Grid item xs={12} md={6} id="dashboard-platforms">
                <ChartCard
                  title="Posts per platform"
                  disabled={!workspaceStatistics?.total}
                  chart={
                    <PieChart
                      series={[
                        {
                          data: workspaceStatistics?.total ? platforms : [],
                          id: 'platforms',
                          highlightScope: { fade: 'global', highlight: 'item' },
                          innerRadius: 0,
                          outerRadius: 80
                        },
                        {
                          data: workspaceStatistics?.total ? types : [],
                          id: 'types',
                          highlightScope: { fade: 'global', highlight: 'item' },
                          innerRadius: 100,
                          outerRadius: 120
                        }
                      ]}
                      margin={{ right: 5 }}
                      slotProps={{ legend: { hidden: true } }}
                      width={400}
                      height={300}
                    />
                  }
                  legend={
                    <ExpandableLegend platforms={platforms} types={types} />
                  }
                />
              </Grid>

              {/* Rated Posts */}
              <Grid item xs={12} md={6}>
                <Stack gap={3} flex={1}>
                  <Stack id="dashboard-rated">
                    <ChartCard
                      disabled={!workspaceStatistics?.totalPosts}
                      title="Rated posts"
                      chart={
                        <PieChart
                          series={[
                            {
                              data: workspaceStatistics?.totalPosts
                                ? reviewedSlices
                                : [],
                              valueFormatter: (_, { dataIndex }) =>
                                String(reviewedPosts[dataIndex].value),
                              innerRadius: 65,
                              outerRadius: 100,
                              startAngle: -105,
                              endAngle: 105,
                              highlightScope: {
                                fade: 'global',
                                highlight: 'item'
                              },
                              highlighted: { additionalRadius: 4 }
                            }
                          ]}
                          margin={{ right: 5 }}
                          slotProps={{ legend: { hidden: true } }}
                          height={300}
                          width={400}
                        >
                          <PieCenterLabel>
                            <Typography
                              fontSize="14px"
                              lineHeight="22px"
                              fontWeight={600}
                              component="text"
                            >
                              {reviewedPosts
                                .map(({ value }) => value)
                                .join(' / ')}
                            </Typography>
                          </PieCenterLabel>
                        </PieChart>
                      }
                    />
                  </Stack>
                  <Stack id="dashboard-sponsored">
                    <ChartCard
                      disabled={!workspaceStatistics?.total}
                      title="Sponsored posts"
                      showDivider={false}
                      legend={
                        <Stack width="100%">
                          <Stack direction="row" justifyContent="space-between">
                            <Typography fontWeight={500}>Posts</Typography>
                            <Typography fontWeight={500}>Sponsored</Typography>
                          </Stack>
                          <Stack>
                            <Stack
                              pt="9.5px"
                              pb="9.5px"
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography>All platform</Typography>
                              <Typography>{`${disclosuredPercentage}%`}</Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={disclosuredPercentage}
                            />
                          </Stack>
                        </Stack>
                      }
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>

            {keywordsLength > 0 && (
              <Grid item xs={12} id="dashboard-keywords">
                <ChartCard
                  disabled={!keywordsLength}
                  actions={
                    keywordsLength <= 7 ? (
                      <></>
                    ) : (
                      <Button
                        variant={keywordSlice ? 'contained' : 'outlined'}
                        text={keywordSlice ? 'Show all' : 'Show less'}
                        onClick={() =>
                          keywordSlice
                            ? setKeywordSlice(undefined)
                            : setKeywordSlice(7)
                        }
                      />
                    )
                  }
                  title="Occurrences per keywords"
                  chart={
                    <BarChart
                      sx={{
                        border:
                          '1px solid var(--Border-Medium, rgba(28, 28, 28, 0.14))'
                      }}
                      dataset={keywordsToShow}
                      colors={['#000']}
                      yAxis={[
                        {
                          scaleType: 'band',
                          dataKey: 'keyword',
                          categoryGapRatio: 0.52
                        } as AxisConfig<'band', unknown, ChartsYAxisProps>
                      ]}
                      series={[{ dataKey: 'value' }]}
                      layout="horizontal"
                      grid={{ vertical: true }}
                      margin={{ left: keywordsMarginLeft }}
                      height={keywordsHeight}
                    />
                  }
                />
              </Grid>
            )}
          </>
        )}
        {isDrawerOpen && (
          <FilterDrawer
            keywordOptions={allKeywords}
            defaultFiltersParams={defaultFilterParams}
            urlParams={urlParams}
            setDrawerOpen={setDrawerOpen}
            isDrawerOpen={isDrawerOpen}
          />
        )}
      </Stack>
    </Page>
  );
};

export default WorkspaceDashboard;
